import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, MapPin, Clock, Mail, Send, MessageSquare, Loader2 } from "lucide-react";
import { companyInfo as mockCompany, galleryImages } from "../data/mock";
import { useCompany } from "../hooks/useContent";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import PageHero from "../components/PageHero";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    honeypot: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name.length < 2 || formData.phone.length < 6 || formData.message.length < 5) {
      toast.error("Completați toate câmpurile obligatorii corect.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.status === 429) {
        toast.error("Prea multe cereri. Încercați din nou mai târziu.");
        return;
      }
      let data;
      try { data = await res.json(); } catch { data = {}; }
      if (res.ok && data.success) {
        setSubmitted(true);
        toast.success(data.message);
        setFormData({ name: "", phone: "", email: "", message: "", honeypot: "" });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        toast.error(data.detail || "Eroare la trimitere. Încercați din nou.");
      }
    } catch {
      toast.error("Eroare de conexiune. Încercați din nou sau sunați-ne direct.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact – Casa Funerară Sfântul Andrei Constanța | NON-STOP 24/7</title>
        <meta name="description" content="Contactați Casa Funerară Sfântul Andrei Constanța. Disponibili NON-STOP 24/7 la telefon, WhatsApp sau prin formularul de contact. Două sedii: Constanța și Lumina." />
        <link rel="canonical" href="https://funeraresfantulandrei.ro/contact" />
        <meta property="og:title" content="Contact – Casa Funerară Sfântul Andrei Constanța" />
        <meta property="og:url" content="https://funeraresfantulandrei.ro/contact" />
      </Helmet>
      <Toaster position="top-right" />
      <PageHero
        image={galleryImages.van}
        title="Contactați-ne"
        subtitle="Suntem disponibili NON-STOP, 24/7. Nu ezitați să ne contactați pentru orice întrebare sau nevoie urgentă."
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Contact" },
        ]}
      />

      {/* Contact Info + Form */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <span className="text-xs uppercase tracking-[4px] text-[#999999] font-medium">
                Informații Contact
              </span>
              <h2
                className="text-2xl sm:text-3xl font-semibold mt-4 mb-10"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Suntem Aici pentru Dumneavoastră
              </h2>

              <div className="space-y-8">
                {/* Phone */}
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0f0f0f] flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#0f0f0f] mb-2">Telefon NON-STOP</h3>
                    <a
                      href={`tel:${mockCompany.phone}`}
                      className="text-lg font-semibold text-[#0f0f0f] no-underline hover:text-[#555555] transition-colors block mb-1"
                    >
                      {mockCompany.phone}
                    </a>
                    <a
                      href={`tel:${mockCompany.phoneSecondary}`}
                      className="text-sm text-[#999999] no-underline hover:text-[#555555] transition-colors"
                    >
                      {mockCompany.phoneSecondary}
                    </a>
                  </div>
                </div>

                {/* Schedule */}
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0f0f0f] flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#0f0f0f] mb-2">Program</h3>
                    <p className="text-sm text-[#666666]">NON-STOP 24/7</p>
                    <p className="text-xs text-[#999999] mt-1">Inclusiv sărbători și zile libere</p>
                  </div>
                </div>

                {/* Addresses */}
                {mockCompany.addresses.map((addr, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0f0f0f] flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-[#0f0f0f] mb-2">{addr.name}</h3>
                      <p className="text-sm text-[#666666]">
                        {addr.street}{addr.postalCode ? `, ${addr.postalCode}` : ''} {addr.city}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#FAFAF8] p-8 lg:p-10 border border-[#E8E4DC]">
                <div className="flex items-center gap-3 mb-8">
                  <MessageSquare className="w-5 h-5 text-[#999999]" />
                  <h3
                    className="text-xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Trimite un Mesaj
                  </h3>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot anti-spam - hidden from users */}
                  <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
                    <input type="text" name="honeypot" tabIndex={-1} autoComplete="off" value={formData.honeypot} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[#999999] mb-2">
                      Nume Complet
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#e0e0e0] text-sm text-[#0f0f0f] focus:outline-none focus:border-[#0f0f0f] transition-colors placeholder:text-[#bbb]"
                      placeholder="Numele dumneavoastră"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[#999999] mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-[#e0e0e0] text-sm text-[#0f0f0f] focus:outline-none focus:border-[#0f0f0f] transition-colors placeholder:text-[#bbb]"
                      placeholder="Numărul de telefon"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[#999999] mb-2">
                      Email <span className="font-normal text-[#bbb]">(opțional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-[#e0e0e0] text-sm text-[#0f0f0f] focus:outline-none focus:border-[#0f0f0f] transition-colors placeholder:text-[#bbb]"
                      placeholder="Adresa de email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[#999999] mb-2">
                      Mesaj
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-white border border-[#e0e0e0] text-sm text-[#0f0f0f] focus:outline-none focus:border-[#0f0f0f] transition-colors placeholder:text-[#bbb] resize-none"
                      placeholder="Descrieți pe scurt ce aveți nevoie..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || submitted}
                    className="w-full flex items-center justify-center gap-2 bg-[#0f0f0f] text-white py-3.5 text-sm font-semibold tracking-wide cursor-pointer hover:bg-[#1a1a1a] transition-colors border-none disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ borderRadius: "var(--radius-pill)" }}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {loading ? "Se trimite..." : submitted ? "Trimis cu succes!" : "Trimite Mesajul"}
                  </button>
                  <p className="text-xs text-[#999999] text-center">
                    Pentru urgențe, vă rugăm să ne sunați direct la{" "}
                    <a href={`tel:${mockCompany.phone}`} className="text-[#0f0f0f] font-semibold no-underline">
                      {mockCompany.phone}
                    </a>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maps Section */}
      <section className="section-spacing bg-[#FAFAF8]">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2
              className="text-2xl sm:text-3xl font-semibold"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Locațiile Noastre
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockCompany.addresses.map((addr, i) => (
              <div key={i} className="bg-white border border-[#E8E4DC] overflow-hidden rounded-2xl">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#0f0f0f] mb-1">{addr.name}</h3>
                  <p className="text-sm text-[#999999]">
                    {addr.street}{addr.postalCode ? `, ${addr.postalCode}` : ''} {addr.city}
                  </p>
                </div>
                <div className="map-container">
                  <iframe
                    src={addr.mapEmbed}
                    title={addr.name}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                {addr.mapLink && (
                  <div className="p-4 text-center">
                    <a
                      href={addr.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold no-underline transition-all"
                      style={{ color: "var(--gold)" }}
                      data-testid={`map-directions-${i}`}
                    >
                      <MapPin className="w-4 h-4" />
                      Obține Direcții
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Sună Acum – Disponibil NON-STOP
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Nu ezitați să ne contactați, indiferent de oră. Suntem aici să vă ajutăm.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${mockCompany.phone}`}
              className="flex items-center gap-3 bg-white text-[#0f0f0f] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
              style={{ borderRadius: "var(--radius-pill)" }}
            >
              <Phone className="w-4 h-4" />
              {mockCompany.phone}
            </a>
            <a
              href={`https://wa.me/${mockCompany.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Bună ziua, am nevoie de informații despre serviciile funerare.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium tracking-wide no-underline transition-colors"
            >
              Sau scrie pe WhatsApp →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
